import { ApiError } from "@/api/errors";
import type { RecognitionErrorEnvelope } from "@/api/types";
import { useAuthStore } from "@/stores/auth";

export interface ApiRequestInit extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  // Skip JSON content-type / serialization (for FormData / multipart).
  raw?: boolean;
}

/** Base URL — same origin in dev (proxied via Vite), can be overridden in tests. */
function getBaseUrl(): string {
  const envBase = import.meta.env?.VITE_API_BASE_URL;
  return typeof envBase === "string" && envBase.length > 0 ? envBase : "";
}

function buildHeaders(init: ApiRequestInit): Headers {
  const headers = new Headers(init.headers ?? {});
  if (!init.raw && init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  return headers;
}

function buildBody(init: ApiRequestInit): BodyInit | null | undefined {
  if (init.body === undefined) return undefined;
  if (init.raw) return init.body as BodyInit;
  return JSON.stringify(init.body);
}

/**
 * FastAPI error response shape (one of three flavours we see in the wild):
 *   1. Our domain envelope         — ``{code, message, details}``
 *   2. FastAPI HTTPException       — ``{detail: "string"}``
 *   3. Pydantic validation error   — ``{detail: [{loc, msg, type, ...}, ...]}``
 *
 * Endpoints that route through the global ``RequestValidationError``
 * handler (see ``src/recognition/main.py``) collapse #3 into #1, but
 * not every legacy/3rd-party error path does — we still need to render
 * something useful when the raw Pydantic list arrives. See
 * TaskMe 45826db6 follow-up: the original symptom was the FE showing
 * "Unprocessable Entity" because this parser dropped the list.
 */
interface PydanticValidationItem {
  loc?: unknown[];
  msg?: string;
  type?: string;
}

function summarizeValidationList(items: PydanticValidationItem[]): string {
  const first: PydanticValidationItem | undefined = items[0];
  if (first === undefined) return "Validation error";
  const item: PydanticValidationItem = first;
  const rawMsg = typeof item.msg === "string" ? item.msg : "Validation error";
  // Pydantic prefixes ``Value error, `` to messages from field_validators;
  // strip it for the operator-facing render.
  const msg = rawMsg.startsWith("Value error, ")
    ? rawMsg.slice("Value error, ".length)
    : rawMsg;
  const locParts = (item.loc ?? []).filter(
    (p) => typeof p === "string" && p !== "body" && p !== "query",
  );
  return locParts.length > 0 ? `${locParts.join(".")}: ${msg}` : msg;
}

async function parseErrorEnvelope(
  response: Response,
): Promise<RecognitionErrorEnvelope> {
  try {
    const data =
      (await response.json()) as Partial<RecognitionErrorEnvelope> & {
        detail?: string | PydanticValidationItem[];
      };

    if (typeof data.code === "string" || typeof data.message === "string") {
      // Domain envelope (or partial — fill in defaults).
      return {
        code:
          typeof data.code === "string" ? data.code : `http_${response.status}`,
        message:
          typeof data.message === "string" ? data.message : response.statusText,
        details:
          typeof data.details === "object" && data.details !== null
            ? data.details
            : {},
      };
    }

    if (typeof data.detail === "string") {
      return {
        code: data.detail,
        message: data.detail,
        details: {},
      };
    }

    if (Array.isArray(data.detail)) {
      return {
        code: "validation_error",
        message: summarizeValidationList(data.detail),
        details: { errors: data.detail },
      };
    }

    return {
      code: `http_${response.status}`,
      message: response.statusText || "Network error",
      details: {},
    };
  } catch {
    return {
      code: `http_${response.status}`,
      message: response.statusText || "Network error",
      details: {},
    };
  }
}

function isJsonResponse(response: Response): boolean {
  const ct = response.headers.get("Content-Type");
  if (ct === null) return false;
  return ct.toLowerCase().includes("application/json");
}

function isEmptyBody(response: Response): boolean {
  if (response.status === 204 || response.status === 205) return true;
  const len = response.headers.get("Content-Length");
  if (len === "0") return true;
  return false;
}

// Страницы, которые работают без сессии. На них 401 — обычный ответ, и
// уводить с них некуда: попытка увела бы на /login с самого /login и дала
// бесконечную перезагрузку.
const PUBLIC_PATHS = ["/login", "/accept-invite", "/reset-password"];

// Ручки, для которых 401 означает «неверные данные», а не «сессия кончилась».
const AUTH_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/accept-invite",
  "/api/auth/reset-password",
];

// Один уход на вход на всю загрузку страницы: реестр открывает несколько
// запросов сразу, и без этого флага каждый из них добавил бы свою
// перезагрузку.
let bouncingToLogin = false;

/** Только для тестов: сбросить флаг однократного ухода на вход. */
export function resetUnauthorizedBounce(): void {
  bouncingToLogin = false;
}

/**
 * Реакция на 401: сессии больше нет — забыть пользователя и уйти на вход.
 *
 * Без этого мёртвая сессия выглядела как пустое приложение: проверка «вошёл
 * ли ты» живёт в памяти браузера, поэтому интерфейс продолжал считать
 * человека вошедшим, все запросы отвечали 401, а таблицы просто
 * отрисовывались пустыми — без единого намёка, что надо войти заново.
 *
 * Уходим полной перезагрузкой, а не переходом внутри приложения: вместе с
 * сессией нужно выбросить и весь кэш запросов, иначе на странице входа
 * останутся чужие данные.
 */
function handleUnauthorized(path: string): void {
  if (AUTH_ENDPOINTS.some((p) => path.startsWith(p))) return;
  if (typeof window === "undefined") return;
  if (PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) return;
  if (bouncingToLogin) return;
  bouncingToLogin = true;
  useAuthStore.getState().clear();
  window.location.assign("/login");
}

/** Typed fetch wrapper. credentials: 'include' is mandatory for cookie-session. */
export async function apiFetch<T>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const url = `${getBaseUrl()}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: init.method ?? "GET",
      credentials: "include",
      headers: buildHeaders(init),
      body: buildBody(init),
      signal: init.signal,
      cache: init.cache,
      mode: init.mode,
      redirect: init.redirect,
    });
  } catch (cause) {
    // fetch() rejects on offline, DNS, CORS, AbortError. Surface as a typed
    // envelope so React Query's retry policy & callers see the same shape
    // they get for HTTP errors. status=0 marks "no response at all".
    throw new ApiError({
      code: "network_error",
      message: "Network request failed",
      status: 0,
      cause,
    });
  }
  if (!response.ok) {
    const envelope = await parseErrorEnvelope(response);
    if (response.status === 401) {
      handleUnauthorized(path);
    }
    throw new ApiError({
      code: envelope.code,
      message: envelope.message,
      status: response.status,
      details: envelope.details ?? {},
    });
  }
  if (isEmptyBody(response) || !isJsonResponse(response)) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
